import React from 'react';
import {
  Container,
  VStack,
  Box,
  Text,
  Spinner,
  useColorModeValue,
  Divider,
  IconButton,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { findNotificationDocIdByPushId } from '../utils/notificationUtils';
import { useNotifications } from '../hooks';
import NotificationItem from '../components/notifications/NotificationItem';
import type { Notification } from '../types/notification';

const NotificationsPage: React.FC = () => {
  const { notifications, loading, handleNotificationClick } = useNotifications();
  const bg = useColorModeValue('white', 'gray.800');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const openPushId = searchParams.get('openPushId');
    if (!openPushId) return;

    let cancelled = false;

    const tryResolve = async () => {
      try {
        const docId = await findNotificationDocIdByPushId(openPushId, 6, 400);
        if (cancelled) return;
        if (docId) {
          navigate(`/notifications/${docId}`);
        } else {
          console.log('[NotificationsPage] openPushId present but doc not found yet:', openPushId);
        }
      } catch (err) {
        console.error('[NotificationsPage] Error resolving openPushId:', err);
      }
    };

    tryResolve();

    return () => { cancelled = true; };
  }, [searchParams, navigate]);

  return (
    <Container maxW="container.md" py={4}>
      <VStack spacing={4} align="stretch">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <IconButton aria-label="Back" icon={<ArrowBackIcon />} variant="ghost" onClick={() => navigate('/dashboard')} />
          <Box textAlign="center">
            <Text fontSize="2xl" fontWeight="bold">Notifications</Text>
            <Text fontSize="xs" color="gray.500">(all unread + today's sent)</Text>
          </Box>
          <Box width="40px" />
        </Box>

        <Box bg={bg} borderRadius="lg" p={2} boxShadow="sm">
          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" py={10}>
              <Spinner size="lg" />
            </Box>
          ) : notifications.length === 0 ? (
            <Box p={4}>
              <Text fontWeight="bold" mb={2}>No notifications found.</Text>
              <Text fontSize="xs" color="gray.500">
                This could mean:
              </Text>
              <Text fontSize="xs" color="gray.500" ml={2}>
                • No notifications exist for your user ID
              </Text>
              <Text fontSize="xs" color="gray.500" ml={2}>
                • Notifications were filtered out (read + old)
              </Text>
              <Text fontSize="xs" color="gray.500" ml={2}>
                • userId mismatch in database
              </Text>
            </Box>
          ) : (
            <VStack spacing={0} align="stretch">
              {notifications.map((n: Notification, idx: number) => (
                <React.Fragment key={n.id}>
                  <NotificationItem
                    notification={n}
                    onClick={() => handleNotificationClick(n)}
                  />
                  {idx < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </Container>
  );
};

export default NotificationsPage;
